import { useEffect, useId, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { formatEuroFr } from '../utils/editProductOutcome'
import {
  articleCountLabel,
  isSharedCartUnavailable,
  sharedCartPreviewItems,
} from '../utils/sharedCartPresentation'
import './SharedCartAttachment.css'

const playedAttachmentKeys = new Set()

function Thumb({ item, index, enter }) {
  const [failed, setFailed] = useState(!item?.image_url)
  const empty = !item?.name && !item?.image_url
  return (
    <div
      className={`sca-thumb sca-thumb-${index}${enter ? ' is-enter' : ''}${empty ? ' is-empty' : ''}`}
      title={item?.name || undefined}
    >
      {failed || empty ? (
        <span className="sca-thumb-fallback" aria-hidden="true" />
      ) : (
        <img src={item.image_url} alt="" onError={() => setFailed(true)} />
      )}
    </div>
  )
}

function BasketMark({ uid }) {
  const meshId = `${uid}-mesh`
  const bodyId = `${uid}-body`
  return (
    <svg className="sca-mark" viewBox="0 0 140 96" aria-hidden="true" focusable="false">
      <defs>
        <pattern id={meshId} width="7" height="7" patternUnits="userSpaceOnUse">
          <path d="M0 7 L7 0 M-2 2 L2 -2 M5 9 L9 5" stroke="currentColor" strokeWidth="0.7" opacity="0.55" />
        </pattern>
        <linearGradient id={bodyId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.28)" />
          <stop offset="55%" stopColor="rgba(180,200,220,0.1)" />
          <stop offset="100%" stopColor="rgba(20,24,32,0.22)" />
        </linearGradient>
      </defs>
      <ellipse className="sca-ground" cx="70" cy="88" rx="38" ry="5" />
      <path className="sca-handle-wire" d="M32 36 C22 6 56 3 60 36" />
      <path className="sca-handle-wire" d="M80 35 C88 3 120 7 108 38" />
      <polygon className="sca-side" points="28,38 40,38 46,82 34,82" />
      <polygon className="sca-wall" points="28,38 112,36 98,82 40,84" fill={`url(#${bodyId})`} />
      <polygon className="sca-mesh" points="32,40 108,38 96,80 44,82" fill={`url(#${meshId})`} />
      <path className="sca-front-lip" d="M40 84 L98 82" />
      <ellipse className="sca-opening-fill" cx="70" cy="36" rx="44" ry="12" />
      <ellipse className="sca-opening" cx="70" cy="36" rx="44" ry="12" />
      <ellipse className="sca-inner" cx="70" cy="39" rx="31" ry="7" />
    </svg>
  )
}

export default function SharedCartAttachment({
  cart,
  sharedCartId,
  own = false,
  variant = 'message',
  playKey,
  onOpen,
}) {
  const reduced = usePrefersReducedMotion()
  const uid = `sca${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const rootRef = useRef(null)
  const pointerStartRef = useRef(null)
  const key = playKey || `${variant}:${sharedCartId || cart?.id || cart?.name || 'anon'}`
  const alreadyPlayed = playedAttachmentKeys.has(key)
  const playedRef = useRef(alreadyPlayed)
  const [enter, setEnter] = useState(false)
  const [visible, setVisible] = useState(false)
  const unavailable = isSharedCartUnavailable(cart, sharedCartId)
  const thumbs = unavailable ? [] : sharedCartPreviewItems(cart)
  const empty = !unavailable && (Number(cart?.items_count) || thumbs.length) === 0
  const name = unavailable ? 'Panier indisponible' : (cart?.name || 'Panier')
  const shownThumbs = unavailable || empty ? [] : thumbs.slice(0, 3)

  useEffect(() => {
    const node = rootRef.current
    if (!node) return undefined
    const io = new IntersectionObserver(([entry]) => {
      const onScreen = Boolean(entry?.isIntersecting)
      setVisible(onScreen)
      if (!onScreen || reduced || playedRef.current) return
      playedRef.current = true
      playedAttachmentKeys.add(key)
      setEnter(true)
    }, { threshold: 0.45 })
    io.observe(node)
    return () => io.disconnect()
  }, [key, reduced])

  return (
    <article
      ref={rootRef}
      onPointerDown={(event) => {
        pointerStartRef.current = { x: event.clientX, y: event.clientY }
      }}
      className={[
        'sca',
        own ? 'is-own' : '',
        unavailable ? 'is-unavailable' : '',
        empty ? 'is-empty' : '',
        reduced ? 'is-reduced' : '',
        enter ? 'is-enter' : '',
        visible ? 'is-visible' : '',
        variant === 'feed' ? 'is-feed' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="sca-scene" aria-hidden="true">
        <div className="sca-rig">
          <BasketMark uid={uid} />
          <div className="sca-thumbs">
            {shownThumbs.map((item, index) => (
              <Thumb key={item.id || index} item={item} index={index} enter={enter && !reduced} />
            ))}
          </div>
        </div>
      </div>
      <div className="sca-copy">
        <h3 className="sca-name" title={name}>{name}</h3>
        {unavailable ? (
          <p className="sca-meta">Ce panier a été supprimé ou n’est plus accessible.</p>
        ) : empty ? (
          <p className="sca-meta">Panier vide</p>
        ) : (
          <p className="sca-meta">
            {articleCountLabel(cart?.items_count)}
            <span aria-hidden="true"> · </span>
            {formatEuroFr(cart?.total)}
          </p>
        )}
        <button
          type="button"
          className="sca-open"
          disabled={unavailable}
          onClick={(event) => {
            const start = pointerStartRef.current
            if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) {
              event.preventDefault()
              return
            }
            if (!unavailable) onOpen?.(cart?.id || sharedCartId, cart?.name)
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M4 7h16M4 12h10M4 17h16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="18" cy="12" r="2.2" fill="currentColor" />
          </svg>
          Aperçu
        </button>
      </div>
    </article>
  )
}
